import type {ReactElement, ReactNode} from 'react';
import { useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {FlatIconColor, FlatIconColorType, IconShape, IconSize, IconType} from "@Components/icon-v2/icon.types";
import {Button, ButtonType, ButtonSize} from '@Components/button';
import useWindowSize from '@/hooks/useWindowSize';
import {isMobileScreen} from '@Libraries/login-signup-library';
import styles from './inactive-user-password-reset-form.module.scss';
import {getAssetUrl} from "@Utils/s3.util";
import {useAppSelector} from "@/hooks";
import {openErrorModal} from "@Modals/error-modal";
import {openMessageModal} from "@Modals/message-modal";

interface InactiveUserPasswordResetFormProps {
  isFormInModal?: boolean;
}

interface ErrorType {
  status: string;
  message: string;
  code: string;
  data: null;
}

export function InactiveUserPasswordResetForm({isFormInModal = false}: InactiveUserPasswordResetFormProps): ReactElement<any> {

    const windowScreenSize = useWindowSize();

    const [delayBeforeResend, setDelayBeforeResend] = useState(30);
    const [isActiveTimer, setIsActiveTimer] = useState(true);

  const sniperLink = useAppSelector((state) => {
    return state.loginPage.inactiveUserPasswordResetSniperLink;
  });

  useEffect(() => {
    if (!isActiveTimer) return;

    const interval = setInterval(() => {
      setDelayBeforeResend((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setIsActiveTimer(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActiveTimer]);

  useEffect(() => {
    if (isActiveTimer) {
      setDelayBeforeResend(30);
    }
  }, [isActiveTimer]);

    const getHeaderIconSize = (): IconSize => {

        if (isFormInModal) {
          if (isMobileScreen(windowScreenSize.windowWidth)) {
            return IconSize.SIZE_ICON_20;
          }
          return IconSize.SIZE_ICON_24;
        }

        if (isMobileScreen(windowScreenSize.windowWidth)) {
            return IconSize.SIZE_ICON_24;
        }

        return IconSize.SIZE_ICON_32;
    }

  const getEmailSniperIconAndTextByLink = (link: string): [string, string] => {
    if (link.includes('mail.google.com')) {
      return [getAssetUrl('gmail-mail-logo.svg'), window.i18next.t('pmwjs_open_gmail')];
    }

    if (link.includes('outlook.live.com')) {
      return [getAssetUrl('outlook-mail-logo.svg'), window.i18next.t('pmwjs_open_outlook')];
    }

    if (link.includes('mail.yahoo.com')) {
      return [getAssetUrl('yahoo-mail-logo.svg'), window.i18next.t('pmwjs_open_yahoo')];
    }

    if (link.includes('mail.proton.me')) {
      return [getAssetUrl('proton-mail-logo.svg'), window.i18next.t('pmwjs_open_proton_mail')];
    }

    if (link.includes('www.icloud.com')) {
      return [getAssetUrl('icloud-mail-logo.svg'), window.i18next.t('pmwjs_open_icloud_mail')];
    }

    if (link.includes('mail.aol.com')) {
      return [getAssetUrl('aol-mail-logo.svg'), window.i18next.t('pmwjs_open_aol')];
    }
    return ['', window.i18next.t('pmwjs_open_mail')];
  }

    const getCustomText = (): ReactElement => {

      const [iconImg, text] = getEmailSniperIconAndTextByLink(sniperLink ?? '');

        return (
            <div className={'flex-center'}>
                {iconImg === '' ? <Icon icon={'icon-envelope'} size={IconSize.SIZE_ICON_16} className={'content-body'} /> : <img height={16} width={16} src={iconImg} alt="email-logo" />}

                <Text
                    val={text}
                    size={TextSize.XSMALL}
                    bold={true}
                    className={`content-body ${styles.customText}`}
                />

                <Icon
                    icon={'icon-external-link'}
                    size={IconSize.SIZE_ICON_16}
                    className={'content-body'}
                />
            </div>
        );
    }

    const getContainerClasses = (): string => {
        if (isFormInModal) {
          return `flex-center`;
        }

        if (isMobileScreen(windowScreenSize.windowWidth)) {
            return `flex-center ${styles.container}`;
        }

        return 'flex-items-start';
    }

    const getDescriptionTextSize = (): TextSize => {
        if (isFormInModal || isMobileScreen(windowScreenSize.windowWidth)) {
            return TextSize.XSMALL;
        }

        return TextSize.SMALL;
    }

    const onExternalEmailClick = (): void => {
      if (sniperLink) {
        window.open(sniperLink, '_blank');
      }
    }

    const getCountdownValue = (): string => {
      if (isActiveTimer) {
        return '<a class="content-disabled">(' + delayBeforeResend.toString() + 's)</a>';
      }

      return '';
    }


    const resendInactivePasswordResetEmail = async (): Promise<void> => {
      try {
        await window.PMW.writeLocal('authenticate/resendInactiveUserPasswordResetEmail', {});
        openMessageModal({
          title: window.i18next.t('pmwjs_password_reset_email_sent'),
          text: window.i18next.t('pmwjs_inactive_user_password_reset_email_resent'),
        });
      } catch (e) {
        const error = e as ErrorType;
        openErrorModal({
          message: error.message ?? window.i18next.t('pmwjs_something_wrong_reload_page'),
          log: false
        });
      }

      setIsActiveTimer(true);
    }
  const onResendEmailClick = (): void => {
    if (delayBeforeResend > 0) {
      return;
    }

    void resendInactivePasswordResetEmail();
  }

  const getDisabledResendEmailText = (): ReactNode => {
      return (
          <Text
              val={window.i18next.t('pmwjs_inactive_user_password_reset_email_not_sent_disabled') + ' ' + getCountdownValue()}
              dangerouslySetInnerHTML={true}
              size={TextSize.XXSMALL}
              className={`content-body spacing-m-t-3 ${styles.footerText} ${styles.enabledTimer}`}
          />
      );
  }

  const getEnabledResendEmailText = (): ReactNode => {
    return (
        <Text
            val={window.i18next.t('pmwjs_inactive_user_password_reset_email_not_sent_enabled')}
            dangerouslySetInnerHTML={true}
            size={TextSize.XXSMALL}
            className={`content-body spacing-m-t-3 ${styles.footerText} ${styles.disabledTimer}`}
            onClick={onResendEmailClick}
        />
    );
  }

  const getHeaderTextClassNames = (): string => {
      if (isFormInModal) {
        return `content-heading ${styles.modalHeaderText}`;
      }

      return `content-heading ${styles.headerText}`;
  }

  const getDescriptionTextClassNames = (): string => {
      if (isFormInModal) {
        return `content-body ${styles.modalDescriptionText}`;
      }
      return `content-body ${styles.descriptionText}`;
  }

    return (
        <div className={`flexbox flex-v-row ${getContainerClasses()}`}>
            <Icon
                icon={'icon-email-repo-normal'}
                size={getHeaderIconSize()}
                shape={IconShape.CIRCLE}
                className={`${styles.headerIcon} flexbox`}
                type={IconType.FLAT}
                flatIconType={{color: FlatIconColor.SUCCESS, type: FlatIconColorType.DEFAULT}}
            />

            <Text
                val={window.i18next.t('pmwjs_welcome_back')}
                className={getHeaderTextClassNames()}
            />

            <Text
                val={window.i18next.t('pmwjs_inactive_user_password_reset_description')}
                size={getDescriptionTextSize()}
                className={getDescriptionTextClassNames()}
            />

            <Button
                type={ButtonType.SECONDARY}
                size={ButtonSize.MEDIUM}
                customText={getCustomText()}
                isFullWidth={isMobileScreen(windowScreenSize.windowWidth)}
                onClick={onExternalEmailClick}
            />

          {isActiveTimer ? getDisabledResendEmailText() : getEnabledResendEmailText()}

        </div>
    );
}